import React, { useState } from "react";
import { Box, LoadingOverlay } from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { SettingsPageLayout, SettingsPageHeader, SettingsTabs } from "./common";
import type { SettingsTabItem } from "./common";
import SubscriptionPrompt from "./SubscriptionPrompt";
import BusinessInformationTab from "./studio/BusinessInformationTab";
import BusinessHoursTab from "./studio/BusinessHoursTab";
import BillingStatusTab from "./studio/BillingStatusTab";
import BrandingTab from "./studio/BrandingTab";
import AdvancedSettingsTab from "./studio/AdvancedSettingsTab";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import usePermissions from "../../hooks/usePermissions";

type EditingSection = "business" | "hours" | "advanced" | "branding" | null;

interface StudioManagementProps {
  /** App-level salon-update handler: merges the (partial) PUT response into
   *  context and refreshes /access. */
  onSubmit: (response: any, arg2?: boolean, opts?: any) => void;
}

export default function StudioManagement({ onSubmit }: StudioManagementProps) {
  const { updateSalon } = useApp();
  const { businessInfo, refreshBusinessInfo } = useSalon() as {
    businessInfo: any;
    refreshBusinessInfo: () => Promise<any>;
  };
  const perms = usePermissions();
  const studioPerms = perms?.settingsStudio;
  const readOnly = studioPerms?.canEdit === false;
  const canSeeBilling = studioPerms?.canSeeBilling !== false;

  const [searchParams, setSearchParams] = useSearchParams();
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const [saving, setSaving] = useState(false);

  const tabs: SettingsTabItem[] = [
    { value: "business", label: "Business Information" },
    { value: "hours", label: "Business Hours" },
    ...(canSeeBilling ? [{ value: "billing", label: "Billing Status" }] : []),
    { value: "branding", label: "Branding" },
    { value: "advanced", label: "Advanced Settings" },
  ];

  const requestedTab = searchParams.get("tab") || "business";
  const activeTab = tabs.some((t) => t.value === requestedTab)
    ? requestedTab
    : "business";

  const handleTabChange = (value: string) => {
    // Leaving a section abandons any in-progress edit in it.
    setEditingSection(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  };

  // One partial PUT /business-locations for whichever section is saving. The app-level
  // handler merges the response (PUT returns only changed fields) and refreshes
  // /access so business_vertical/staffing_model stay in sync.
  const handleUpdate = async (payload: Record<string, any> | null) => {
    if (!payload) {
      onSubmit(null);
      setEditingSection(null);
      return;
    }
    try {
      setSaving(true);
      const response = await updateSalon(payload);
      if (response) {
        onSubmit(response, false, { skipSalonPersist: true });
        setEditingSection(null);
      }
    } catch (error) {
      console.error("Error updating business settings:", error);
    } finally {
      setSaving(false);
    }
  };

  // Logo uploads go straight to the SSG create-image endpoint (via
  // uploadImage inside BrandingTab), not the salon PUT — the endpoint persists
  // the logo itself. So on success we just refresh the salon record to pick up
  // the new logo_url and leave edit mode.
  const handleLogoSaved = async () => {
    await refreshBusinessInfo();
    setEditingSection(null);
  };

  return (
    <SettingsPageLayout
      header={
        <SettingsPageHeader
          title="My Business"
          subtitle="Business profile and studio configuration"
        />
      }
    >
      <LoadingOverlay visible={!businessInfo} />

      {businessInfo?.status !== "Active" && canSeeBilling && (
        <SubscriptionPrompt />
      )}

      <SettingsTabs
        items={tabs}
        value={activeTab}
        onChange={handleTabChange}
        ariaLabel="My Business sections"
      />

      <Box>
        {activeTab === "business" && (
          <BusinessInformationTab
            businessInfo={businessInfo}
            editing={editingSection === "business"}
            saving={saving}
            readOnly={readOnly}
            onEdit={() => setEditingSection("business")}
            onCancel={() => setEditingSection(null)}
            onSave={handleUpdate}
          />
        )}

        {activeTab === "hours" && (
          <BusinessHoursTab
            businessInfo={businessInfo}
            editing={editingSection === "hours"}
            saving={saving}
            readOnly={readOnly}
            onEdit={() => setEditingSection("hours")}
            onCancel={() => setEditingSection(null)}
            onSave={handleUpdate}
          />
        )}

        {activeTab === "billing" && canSeeBilling && (
          <BillingStatusTab businessInfo={businessInfo} />
        )}

        {activeTab === "branding" && (
          <BrandingTab
            businessInfo={businessInfo}
            editing={editingSection === "branding"}
            readOnly={readOnly}
            onEdit={() => setEditingSection("branding")}
            onCancel={() => setEditingSection(null)}
            onSaved={handleLogoSaved}
          />
        )}

        {activeTab === "advanced" && (
          <AdvancedSettingsTab
            businessInfo={businessInfo}
            editing={editingSection === "advanced"}
            saving={saving}
            readOnly={readOnly}
            onEdit={() => setEditingSection("advanced")}
            onCancel={() => setEditingSection(null)}
            onSave={handleUpdate}
          />
        )}
      </Box>
    </SettingsPageLayout>
  );
}
